import React, { useEffect, useRef, useState } from "react";
import { ethers } from "ethers";
import { useWeb3 } from "../../context/Web3Context";
import { useAdminTx } from "../../hooks/useContract";
import { useNotification } from "../../hooks/useNotification";
import { shortAddress } from "../../utils/format";
import {
  STORAGE_ADDRESS, LOGIC_ADDRESS, MIGRATOR_ADDRESS,
} from "../../config/contract";
import Button from "../../components/Common/Button";
import { Card } from "../../components/Common/Card";

const ZERO = "0x0000000000000000000000000000000000000000";

// Compare addresses case-insensitively; returns true if both non-zero and match.
function sameAddr(a, b) {
  if (!a || !b) return false;
  if (a === ZERO || b === ZERO) return false;
  return a.toLowerCase() === b.toLowerCase();
}

function AdminLinking() {
  const notify = useNotification();
  const { getContract, isOwner } = useWeb3();
  const { send, pending } = useAdminTx();

  const [chainState, setChainState] = useState({
    loading: true,
    storageOnChain_logic: "",
    storageOnChain_migrator: "",
    migrationLocked: false,
  });
  const [reloadKey, setReloadKey] = useState(0);
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    const load = async () => {
      const contract = getContract();
      if (!contract) return;
      try {
        const [logicAddr, migAddr, locked] = await Promise.all([
          contract.logicContract().catch(() => ZERO),
          contract.migrator().catch(() => ZERO),
          contract.migrationLocked().catch(() => false),
        ]);
        if (!cancelRef.current) {
          setChainState({
            loading: false,
            storageOnChain_logic: logicAddr || ZERO,
            storageOnChain_migrator: migAddr || ZERO,
            migrationLocked: !!locked,
          });
        }
      } catch {
        if (!cancelRef.current) setChainState((s) => ({ ...s, loading: false }));
      }
    };
    load();
    return () => { cancelRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const linkLogic = async () => {
    if (!ethers.utils.isAddress(LOGIC_ADDRESS) || LOGIC_ADDRESS === ZERO) {
      return notify.error("Set logicAddress in networks.js first");
    }
    const contract = getContract();
    if (!contract) return;
    const ok = await send(
      () => contract.setLogicContract(LOGIC_ADDRESS),
      {
        pendingMsg: "Linking Logic contract...",
        successMsg: "Logic contract linked",
        action: "set_logic_contract",
        params: { logic: LOGIC_ADDRESS },
      }
    );
    if (ok) reload();
  };

  const linkMigrator = async () => {
    if (!ethers.utils.isAddress(MIGRATOR_ADDRESS) || MIGRATOR_ADDRESS === ZERO) {
      return notify.error("Set migratorAddress in networks.js first");
    }
    const contract = getContract();
    if (!contract) return;
    const ok = await send(
      () => contract.setMigrator(MIGRATOR_ADDRESS),
      {
        pendingMsg: "Linking Migrator...",
        successMsg: "Migrator linked",
        action: "set_migrator",
        params: { migrator: MIGRATOR_ADDRESS },
      }
    );
    if (ok) reload();
  };

  const unlinkMigrator = async () => {
    if (!window.confirm("Unlink migrator? (sets migrator to zero address)")) return;
    const contract = getContract();
    if (!contract) return;
    const ok = await send(
      () => contract.setMigrator(ZERO),
      {
        pendingMsg: "Unlinking...",
        successMsg: "Migrator unlinked",
        action: "set_migrator",
        params: { migrator: ZERO },
      }
    );
    if (ok) reload();
  };

  const doLockMigration = async () => {
    if (!window.confirm("⚠ Permanently lock migration? This CANNOT be undone.")) return;
    if (!window.confirm("Really sure? Migration will be disabled FOREVER on this contract.")) return;
    const contract = getContract();
    if (!contract) return;
    const ok = await send(
      () => contract.lockMigration(),
      {
        pendingMsg: "Locking migration...",
        successMsg: "Migration permanently locked",
        action: "lock_migration",
      }
    );
    if (ok) reload();
  };

  // Compute link statuses
  const logicMatches = sameAddr(chainState.storageOnChain_logic, LOGIC_ADDRESS);
  const logicSet = chainState.storageOnChain_logic && chainState.storageOnChain_logic !== ZERO;
  const migratorMatches = sameAddr(chainState.storageOnChain_migrator, MIGRATOR_ADDRESS);
  const migratorSet = chainState.storageOnChain_migrator && chainState.storageOnChain_migrator !== ZERO;

  // Overall status
  const allOk = logicMatches;

  return (
    <div>
      <div className="section-header" style={{ marginTop: 0 }}>
        <div className="section-title">Contract Wiring</div>
      </div>

      {/* Overall status banner */}
      <Card className="mb-lg" style={{ borderColor: allOk ? "rgba(34, 255, 160, 0.4)" : "rgba(251, 191, 36, 0.4)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{
            width: 40, height: 40, borderRadius: "50%",
            background: allOk ? "rgba(34, 255, 160, 0.15)" : "rgba(251, 191, 36, 0.15)",
            border: `2px solid ${allOk ? "var(--success)" : "var(--warning)"}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 20, color: allOk ? "var(--success)" : "var(--warning)",
          }}>
            {allOk ? "✓" : "⚠"}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: allOk ? "var(--success)" : "var(--warning)" }}>
              {allOk ? "All Contracts Linked" : "Wiring Needs Attention"}
            </div>
            <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>
              {allOk
                ? "Storage → Logic wired correctly. Users can register/activate/claim."
                : "Logic contract is NOT linked to Storage. Business writes will fail until linked."}
            </div>
          </div>
        </div>
      </Card>

      {/* Storage — always the address the user is connecting to */}
      <div className="section-header">
        <div className="section-title">Storage Contract</div>
      </div>
      <Card className="mb-lg">
        <AddressRow
          label="Address (config = live)"
          address={STORAGE_ADDRESS}
          status="active"
        />
      </Card>

      {/* Logic */}
      <div className="section-header">
        <div className="section-title">Logic Contract</div>
      </div>
      <Card className="mb-lg">
        <AddressRow label="Config (networks.js)" address={LOGIC_ADDRESS} />
        <div style={{ height: 8 }} />
        <AddressRow
          label="On-chain (storage.logicContract)"
          address={chainState.loading ? "…" : (chainState.storageOnChain_logic || ZERO)}
        />

        <div style={{ marginTop: 12 }}>
          {chainState.loading ? (
            <div style={{ textAlign: "center", padding: 8 }}><span className="spinner" /></div>
          ) : logicMatches ? (
            <div style={{
              padding: "8px 12px",
              background: "rgba(34, 255, 160, 0.08)",
              border: "1px solid rgba(34, 255, 160, 0.35)",
              borderRadius: 8,
              color: "var(--success)",
              fontSize: 12,
              display: "flex", alignItems: "center", gap: 6,
            }}>
              ✓ Logic contract is correctly wired to Storage.
            </div>
          ) : (
            <div>
              <div style={{
                padding: "8px 12px",
                background: "rgba(251, 191, 36, 0.08)",
                border: "1px solid rgba(251, 191, 36, 0.35)",
                borderRadius: 8,
                color: "var(--warning)",
                fontSize: 12,
                marginBottom: 8,
              }}>
                ⚠ {logicSet
                  ? "Storage points to a DIFFERENT logic address than networks.js config"
                  : "Storage has no logic contract linked yet"}
              </div>
              {isOwner ? (
                <Button variant="primary" full onClick={linkLogic} loading={pending} disabled={LOGIC_ADDRESS === ZERO}>
                  Link Logic Contract
                </Button>
              ) : (
                <div style={{ fontSize: 11, color: "var(--text-muted)", padding: 4 }}>
                  Only the owner can call setLogicContract().
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Migrator (optional) */}
      <div className="section-header">
        <div className="section-title">Migrator Contract</div>
      </div>
      <Card className="mb-lg">
        <AddressRow label="Config (networks.js)" address={MIGRATOR_ADDRESS} optional />
        <div style={{ height: 8 }} />
        <AddressRow
          label="On-chain (storage.migrator)"
          address={chainState.loading ? "…" : (chainState.storageOnChain_migrator || ZERO)}
          optional
        />

        <div style={{ marginTop: 12 }}>
          {chainState.migrationLocked ? (
            <div style={{
              padding: "8px 12px",
              background: "rgba(139,155,209,0.08)",
              border: "1px solid var(--border-strong)",
              borderRadius: 8,
              color: "var(--text-muted)",
              fontSize: 12,
            }}>
              🔒 Migration is permanently locked on this contract. No further migrator changes possible.
            </div>
          ) : migratorMatches ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "center" }}>
              <div style={{
                padding: "8px 12px",
                background: "rgba(34, 255, 160, 0.08)",
                border: "1px solid rgba(34, 255, 160, 0.35)",
                borderRadius: 8,
                color: "var(--success)",
                fontSize: 12,
              }}>
                ✓ Migrator wired correctly
              </div>
              {isOwner && (
                <Button variant="ghost" size="sm" onClick={unlinkMigrator} loading={pending}>Unlink</Button>
              )}
            </div>
          ) : MIGRATOR_ADDRESS === ZERO && !migratorSet ? (
            <div style={{
              padding: "8px 12px",
              background: "rgba(139,155,209,0.08)",
              border: "1px solid var(--border-strong)",
              borderRadius: 8,
              color: "var(--text-muted)",
              fontSize: 12,
            }}>
              Optional — set migratorAddress in networks.js if you plan to bulk-import users.
            </div>
          ) : (
            <div>
              <div style={{
                padding: "8px 12px",
                background: "rgba(251, 191, 36, 0.08)",
                border: "1px solid rgba(251, 191, 36, 0.35)",
                borderRadius: 8,
                color: "var(--warning)",
                fontSize: 12,
                marginBottom: 8,
              }}>
                ⚠ Migrator mismatch — config vs on-chain differs
              </div>
              {isOwner && MIGRATOR_ADDRESS !== ZERO && (
                <Button variant="primary" full onClick={linkMigrator} loading={pending}>
                  Link Migrator
                </Button>
              )}
            </div>
          )}

          {/* Lock migration button */}
          {isOwner && !chainState.migrationLocked && (
            <div style={{ marginTop: 12, padding: "10px 12px", background: "#050815", border: "1px dashed rgba(255, 77, 109, 0.4)", borderRadius: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--error)", marginBottom: 6 }}>
                🚨 Lock Migration Forever
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8, lineHeight: 1.4 }}>
                Permanently disables all migrate* functions on Storage AND Logic. Use ONLY after migration is fully verified. This action is irreversible.
              </div>
              <Button variant="ghost" size="sm" full onClick={doLockMigration} loading={pending}>
                Permanently Lock Migration
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* USDT approval note */}
      <div style={{
        padding: "10px 12px",
        background: "#050815",
        border: "1px solid var(--border-strong)",
        borderRadius: 10,
        fontSize: 11,
        color: "var(--text-muted)",
        lineHeight: 1.5,
      }}>
        <b style={{ color: "var(--cyan)" }}>Note:</b> Users approve USDT to the <b>Storage</b> address
        (that's where funds land). All business writes (register / activate / retopup / claim / block)
        go to the <b>Logic</b> contract, which pulls USDT through the Storage-gated `pullUsdt`.
        Both contracts must be properly wired for the app to work.
      </div>
    </div>
  );
}

function AddressRow({ label, address, status, optional }) {
  const notify = useNotification();
  const isZero = !address || address === ZERO;
  const isLoading = address === "…";
  const copy = async () => {
    if (isZero || isLoading) return;
    try {
      await navigator.clipboard.writeText(address);
      notify.success("Address copied");
    } catch {}
  };

  return (
    <div>
      <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600, marginBottom: 4 }}>
        {label}
      </div>
      <div
        onClick={copy}
        style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "8px 10px",
          background: "#050815",
          border: `1px solid ${status === "active" ? "var(--cyan-border)" : isZero ? "var(--border-strong)" : "var(--border-strong)"}`,
          borderRadius: 8,
          cursor: isZero || isLoading ? "default" : "pointer",
        }}
        title={isZero ? "Not set" : address}
      >
        <div style={{
          flex: 1,
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: isZero ? "var(--text-muted)" : "var(--cyan)",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {isLoading ? "loading..." : isZero ? (optional ? "(not set — optional)" : "0x0000...0000 (not set)") : address}
        </div>
        {!isZero && !isLoading && (
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: "0.03em",
            color: "var(--text-muted)", padding: "2px 6px",
            background: "rgba(0, 229, 255, 0.08)", borderRadius: 4,
          }}>
            COPY
          </span>
        )}
      </div>
    </div>
  );
}

export default AdminLinking;
